import { FormControl, InputLabel, Select, MenuItem, TextField, FormLabel, RadioGroup, FormControlLabel, Radio, Box } from '@mui/material';
import { Controller, type UseFormRegister, type Control } from 'react-hook-form';

interface ClinicalSurveyFieldsProps {
  register: UseFormRegister<any>;
  control: Control<any>;
}

/**
 * ClinicalSurveyFields - 8 optional survey questions for clinical trials
 */
export function ClinicalSurveyFields({ register, control }: ClinicalSurveyFieldsProps) {
  return (
    <Box>
      {/* Q1: Age Band */}
      <Controller
        name="survey.ageBand"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Age band</InputLabel>
            <Select {...field} value={field.value || ''} label="Age band">
              <MenuItem value="">None</MenuItem>
              <MenuItem value="child">Children (&lt;15)</MenuItem>
              <MenuItem value="young-adult">Young Adults (15-23)</MenuItem>
              <MenuItem value="adult">Adults (23-65)</MenuItem>
              <MenuItem value="senior">Seniors (65+)</MenuItem>
            </Select>
          </FormControl>
        )}
      />

      {/* Q2: Biological Sex */}
      <Controller
        name="survey.biologicalSex"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Biological sex</InputLabel>
            <Select {...field} value={field.value || ''} label="Biological sex">
              <MenuItem value="">None</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
            </Select>
          </FormControl>
        )}
      />

      {/* Q3: Race/Ethnicity */}
      <TextField
        label="Race or ethnicity (if advised)"
        fullWidth
        {...register('survey.ethnicity')}
        sx={{ mb: 2 }}
        InputLabelProps={{ shrink: true }}
      />

      {/* Q4: Symptom Presence */}
      <Controller
        name="survey.symptomPresence"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel>Symptom presence</FormLabel>
            <RadioGroup {...field} value={field.value || ''}>
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        )}
      />

      {/* Q5: Symptom Duration */}
      <Controller
        name="survey.symptomDuration"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Symptom duration</InputLabel>
            <Select {...field} value={field.value || ''} label="Symptom duration">
              <MenuItem value="">None</MenuItem>
              <MenuItem value="<24h">&lt;24 hours</MenuItem>
              <MenuItem value="1-3days">1-3 days</MenuItem>
              <MenuItem value="4-7days">4-7 days</MenuItem>
              <MenuItem value=">7days">&gt;7 days</MenuItem>
            </Select>
          </FormControl>
        )}
      />

      {/* Q7: Prior Test Result */}
      <Controller
        name="survey.priorTestResult"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel>Prior positive/negative test result?</FormLabel>
            <RadioGroup {...field} value={field.value || ''}>
              <FormControlLabel value="positive" control={<Radio />} label="Positive" />
              <FormControlLabel value="negative" control={<Radio />} label="Negative" />
              <FormControlLabel value="none" control={<Radio />} label="No prior test" />
            </RadioGroup>
          </FormControl>
        )}
      />

      {/* Q8: Geographic Region */}
      <TextField
        label="Geographic region (country or NHS trust)"
        fullWidth
        {...register('survey.geographicRegion')}
        placeholder="e.g., United Kingdom, NHS North West"
        sx={{ mb: 2 }}
        InputLabelProps={{ shrink: true }}
      />
    </Box>
  );
}